/**
 * SentryFeedbackButton.jsx
 * 
 * Custom Sentry User Feedback button that:
 * - Only displays for authenticated users
 * - Automatically prefills user's name and email
 * - Positions to avoid covering the footer logo
 * - Uses a modal styled to match the application theme
 */

import React, { useState, useLayoutEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../context/AuthContext';
import { FaBug } from 'react-icons/fa';
import './SentryFeedbackButton.scss';

const SentryFeedbackButton = () => {
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [isOverFooter, setIsOverFooter] = useState(false);

    // Detect if button is over footer to adjust positioning
    // Use useLayoutEffect to calculate position before paint to prevent visual jump
    useLayoutEffect(() => {
        const checkFooterPosition = () => {
            const footer = document.querySelector('.footer');
            if (!footer) return;

            const footerRect = footer.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            // Check if footer is visible in viewport (button would overlap)
            // Footer starts at footerRect.top, button is at bottom: 24px
            // If footer is within ~150px of bottom, shift button
            const footerVisibleHeight = windowHeight - footerRect.top;
            setIsOverFooter(footerVisibleHeight > 0 && footerVisibleHeight < 150);
        };

        // Check immediately on mount (before paint)
        checkFooterPosition();
        
        // Continue checking on scroll and resize
        window.addEventListener('scroll', checkFooterPosition);
        window.addEventListener('resize', checkFooterPosition);

        return () => {
            window.removeEventListener('scroll', checkFooterPosition);
            window.removeEventListener('resize', checkFooterPosition);
        };
    }, []);

    // Only show button for authenticated users
    // Must be after all hooks to follow Rules of Hooks
    if (!user) {
        return null;
    }

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setFeedbackMessage('');
        setSubmitStatus(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFeedbackMessage('');
        setSubmitStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!feedbackMessage.trim()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Capture feedback with prefilled user information
            // Note: Backend returns UserEmail (capital E), not email
            // Field extraction order matches AuthContext for consistency
            const UserEmail = user.userEmail || user.UserEmail || user.email;
            const FullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
            
            await Sentry.captureFeedback(
                {
                    name: FullName,
                    email: UserEmail,
                    message: feedbackMessage,
                },
                {
                    includeReplay: true, // Include session replay if available
                }
            );

            setSubmitStatus('success');
            setTimeout(() => {
                handleCloseModal();
            }, 2000);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error submitting feedback:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Floating Feedback Button */}
            <button
                className={`sentry-feedback-button ${isOverFooter ? 'over-footer' : ''}`}
                onClick={handleOpenModal}
                aria-label="Report a Bug"
                title="Report a Bug"
            >
                <FaBug aria-hidden="true" />
                {/* Visually-hidden label keeps the button accessible without displaying visible text */}
                <span
                    style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }}
                >
                    Report a Bug
                </span>
            </button>

            {/* Custom Feedback Modal */}
            {isModalOpen && (
                <div className="sentry-feedback-modal-overlay" onClick={handleCloseModal}>
                    <div className="sentry-feedback-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sentry-feedback-modal-header">
                            <h2>Report a Bug</h2>
                            <button
                                className="sentry-feedback-modal-close"
                                onClick={handleCloseModal}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="sentry-feedback-form">
                            <p className="sentry-feedback-subtitle">
                                Our team has been notified. If you'd like to help, tell us what happened below.
                            </p>

                            <div className="sentry-feedback-form-group">
                                <label htmlFor="feedback-message">What happened?</label>
                                <textarea
                                    id="feedback-message"
                                    value={feedbackMessage}
                                    onChange={(e) => setFeedbackMessage(e.target.value)}
                                    placeholder="Describe what you were doing when the issue occurred..."
                                    rows={6}
                                    required
                                    disabled={isSubmitting || submitStatus === 'success'}
                                    data-sentry-unmask="true"
                                />
                            </div>

                            {submitStatus === 'success' && (
                                <div className="sentry-feedback-success">
                                    ✓ Your feedback has been sent. Thank you!
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="sentry-feedback-error">
                                    An error occurred while submitting your feedback. Please try again.
                                </div>
                            )}

                            <div className="sentry-feedback-form-actions">
                                <button
                                    type="button"
                                    className="sentry-feedback-button-secondary"
                                    onClick={handleCloseModal}
                                    disabled={isSubmitting}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="sentry-feedback-button-primary"
                                    disabled={isSubmitting || !feedbackMessage.trim() || submitStatus === 'success'}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default SentryFeedbackButton;
